import { LinearProgress, Stack } from "@mui/material";
import take from "lodash/take";
import type { FC } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../auth/AuthContext";
import Actions from "../components/actions/Actions";
import AddFab from "../components/actions/AddFab";
import InfiniteScroll from "../components/infiniteScroll/InfiniteScroll";
import dayjs from "../dayjs";
import { startOfPreviousMonth } from "../misc/dateUtils";
import { useLimit } from "../misc/useLimit";
import { useOpenInNewTab } from "../misc/useOpenInNewTab";
import { useQuery } from "../misc/useQuery";
import CalculationCard from "./CalculationCard";
import TextSearchWithMonth from "./calculationsSearch/TextSearchWithMonth";
import CalculationsCsvExport from "./csvExport/CalculationsCsvExport";
import type { ListCalculationsResponseItemWithCustomer } from "./useCalculations";
import { useCalculations } from "./useCalculations";
import type { CalculationFilters } from "./useCalculationsSearch";

interface Props {
  readonly search: CalculationFilters;
  readonly onSearchChange: (value: Partial<CalculationFilters>) => void;
}

function calculationNameMatches(calculation: ListCalculationsResponseItemWithCustomer, query: string) {
  return query.length === 0 || calculation.name.toLowerCase().includes(query);
}

function calculationStartDateMatches(calculation: ListCalculationsResponseItemWithCustomer, query: string) {
  return query.length === 0 || calculation.startDate.includes(query);
}

function customerMatches(calculation: ListCalculationsResponseItemWithCustomer, customerId: string | null) {
  return !customerId || calculation.customer.id === customerId;
}

function filterCalculations(
  calculations: ListCalculationsResponseItemWithCustomer[],
  query: string,
  customerId: string | null,
) {
  return calculations
    .filter((c) => calculationNameMatches(c, query) || calculationStartDateMatches(c, query))
    .filter((c) => customerMatches(c, customerId));
}

const CalculationsPage: FC<Props> = ({ search, onSearchChange }) => {
  const navigate = useNavigate();
  const { context } = useAuthContext();
  const { openChildInNewTab } = useOpenInNewTab();
  const { limit, loadMore } = useLimit();
  const { calculations, isLoading, updateCalculationNotes } = useCalculations({ from: search.month.format("YYYY-MM") });
  const searchQuery = useQuery();
  useEffect(() => {
    const query = searchQuery.get("query");
    const month = searchQuery.get("month");
    const customerId = searchQuery.get("customerId");
    if (query || month || customerId) {
      onSearchChange({
        text: query ?? "",
        month: month ? dayjs(month, "YYYY-MM") : startOfPreviousMonth(),
        customerId: customerId ?? null,
      });
    }
  }, []);

  const filteredCalculations = filterCalculations(calculations, search.text.toLowerCase(), search.customerId);
  const csvExportEnabled = context?.role === "ADMIN" || context?.role === "AGENT";

  return (
    <Stack
      spacing={1}
      sx={{
        marginTop: 1,
        paddingBottom: 1,
        height: "100%",
      }}
    >
      <TextSearchWithMonth value={search} onChange={onSearchChange} />
      {isLoading && <LinearProgress />}
      <InfiniteScroll
        dataLength={limit < filteredCalculations.length ? limit : filteredCalculations.length}
        fetchData={loadMore}
        hasMore={limit < filteredCalculations.length}
      >
        {take(filteredCalculations, limit).map((o) => (
          <CalculationCard
            key={o.id}
            value={o}
            role={context?.role}
            onClick={() => navigate(o.id)}
            onContextMenu={() => openChildInNewTab(o.id)}
            onUpdateNotes={(notes) => updateCalculationNotes(o.id, notes)}
          />
        ))}
      </InfiniteScroll>
      <Actions>
        {csvExportEnabled && <CalculationsCsvExport />}
        <AddFab onClick={() => navigate("new")} />
      </Actions>
    </Stack>
  );
};

export default CalculationsPage;
